import { useEffect } from 'react';
import { useRouteError, useNavigate } from 'react-router';
import { toError } from './ErrorBoundary';
import { captureException } from '../observability/sentry';

/**
 * Friendly fallback for any unhandled render/loader error inside a route.
 * Prevents react-router's "Unexpected Application Error!" default UI from
 * reaching users.
 */
export function RouteErrorElement() {
  const err = toError(useRouteError());
  const navigate = useNavigate();

  useEffect(() => {
    captureException(err);
    if (import.meta.env.DEV) {
      console.error('Route error', err);
    }
  }, [err]);

  return (
    <div className="flex flex-1 items-center justify-center p-6">
      <div className="w-full max-w-lg rounded-xl border border-border bg-bg-secondary p-6 shadow-xl">
        <p className="text-xs uppercase tracking-widest text-text-muted">Page error</p>
        <h1 className="mt-2 text-xl font-bold text-text-primary">
          Something went wrong on this page
        </h1>
        <p className="mt-2 text-sm text-text-secondary">
          The rest of the app is still working. Try reloading or navigating elsewhere.
        </p>
        {import.meta.env.DEV && (
          <pre className="mt-3 max-h-48 overflow-auto rounded border border-border bg-bg-primary p-2 text-xs text-text-muted">
            {err.message}
          </pre>
        )}
        <div className="mt-5 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded bg-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90"
          >
            Reload page
          </button>
          <button
            type="button"
            onClick={() => navigate('/', { replace: true })}
            className="rounded border border-border px-4 py-2 text-sm font-medium text-text-primary hover:bg-bg-hover"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
